import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  PoModalAction,
  PoModalComponent,
  PoNotificationService,
  PoSelectOption,
  PoTableAction,
  PoTableColumn,
} from '@po-ui/ng-components';
import { forkJoin } from 'rxjs';
import {
  AddReleaseRulesRequest,
  Release,
  ReleaseGoal,
  ReleaseRule,
  Rule,
} from '../../interfaces/release';
import { SonarExtractorService } from '../../services/sonar-extractor.service';

/** Regra do catálogo enriquecida com estado de seleção para o modal */
interface CatalogRuleItem {
  rule:        Rule;
  linked:      boolean;
  selected:    boolean;
  pendingGoal: ReleaseGoal;
}

/** ReleaseRule enriquecida com nome/key da regra para exibição */
interface LinkedRuleRow extends ReleaseRule {
  rule_name:   string;
  rule_key:    string;
  editingGoal: ReleaseGoal;
}

@Component({
  selector: 'app-sonar-extractor-releases',
  templateUrl: './sonar-extractor-releases.component.html',
  styleUrls: ['./sonar-extractor-releases.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SonarExtractorReleasesComponent implements OnInit {
  @ViewChild('modalForm',  { static: true }) modalForm!:  PoModalComponent;
  @ViewChild('modalRules', { static: true }) modalRules!: PoModalComponent;

  // ── Tabela de Releases ──────────────────────────────────────────────────────
  releases: Release[] = [];
  loading = false;
  currentPage = 1;
  readonly pageLimit = 20;
  hasMoreReleases = false;

  // ── Formulário de Criação/Edição ────────────────────────────────────────────
  formMode: 'create' | 'edit' = 'create';
  selectedRelease: Release | null = null;
  formName        = '';
  formDescription = '';
  formStartDate   = '';
  formEndDate     = '';  formIsCurrent   = false;  formLoading     = false;

  // ── Gerenciamento de Regras ─────────────────────────────────────────────────
  managedRelease:   Release | null    = null;
  linkedRules:      LinkedRuleRow[]   = [];
  catalogItems:     CatalogRuleItem[] = [];
  catalogFiltered:  CatalogRuleItem[] = [];
  catalogSearch     = '';
  loadingRules      = false;
  savingRules       = false;
  rulesActiveTab    = 'linked';
  catalogPage       = 1;
  readonly catalogLimit = 100;
  hasMoreCatalog    = false;

  // ── Opções ──────────────────────────────────────────────────────────────────
  readonly goalOptions: PoSelectOption[] = [
    { label: 'Zerar',            value: 'RESOLVE'            },
    { label: 'Zerar ou Reduzir', value: 'RESOLVE_OR_REDUCE' },
  ];

  // ── Colunas ─────────────────────────────────────────────────────────────────
  readonly releasesColumns: PoTableColumn[] = [
    { property: 'id',          label: 'ID',        type: 'number',  width: '60px' },
    { property: 'name',        label: 'Nome',      type: 'string' },
    { property: 'description', label: 'Descrição', type: 'string' },
    { property: 'start_date',  label: 'Início',    type: 'date'   },
    { property: 'end_date',    label: 'Fim',       type: 'date'   },
    { property: 'is_current',  label: 'Atual',     type: 'boolean', width: '80px' },
  ];

  readonly releasesActions: PoTableAction[] = [
    {
      label:  'Editar',
      icon:   'po-icon-edit',
      action: (release: Release) => this.openEditModal(release),
    },
    {
      label:  'Gerenciar Regras',
      icon:   'po-icon-settings',
      action: (release: Release) => this.openRulesModal(release),
    },
    {
      label:  'Excluir',
      icon:   'po-icon-delete',
      type:   'danger',
      action: (release: Release) => this.deleteRelease(release),
    },
  ];

  // ── Ações dos Modais ────────────────────────────────────────────────────────
  modalFormPrimaryAction: PoModalAction = {
    label:   'Criar',
    loading: false,
    action:  () => this.saveRelease(),
  };

  modalFormSecondaryAction: PoModalAction = {
    label:  'Cancelar',
    action: () => this.modalForm.close(),
  };

  modalRulesCloseAction: PoModalAction = {
    label:  'Fechar',
    action: () => this.modalRules.close(),
  };

  constructor(
    private sonarService:  SonarExtractorService,
    private notification:  PoNotificationService,
    private destroyRef:    DestroyRef,
    private cdr:           ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    setTimeout(() => this.loadReleases());
  }

  // ── Carregamento ────────────────────────────────────────────────────────────

  loadReleases(append = false): void {
    this.loading = true;
    this.sonarService
      .getReleases({ page: this.currentPage, limit: this.pageLimit })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.releases       = append ? [...this.releases, ...result.releases] : result.releases;
          this.hasMoreReleases = this.currentPage < result.totalPages;
          this.loading        = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.notification.error('Erro ao carregar as releases.');
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  loadMoreReleases(): void {
    this.currentPage++;
    this.loadReleases(true);
  }

  // ── Formulário de Criação/Edição ────────────────────────────────────────────

  openCreateModal(): void {
    this.formMode        = 'create';
    this.selectedRelease = null;
    this.formName        = '';
    this.formDescription = '';
    this.formStartDate   = '';
    this.formEndDate     = '';
    this.formIsCurrent   = false;
    this.modalFormPrimaryAction.label = 'Criar';
    this.modalForm.open();
  }

  openEditModal(release: Release): void {
    this.formMode        = 'edit';
    this.selectedRelease = release;
    this.formName        = release.name;
    this.formDescription = release.description ?? '';
    this.formStartDate   = release.start_date   ?? '';
    this.formEndDate     = release.end_date      ?? '';
    this.formIsCurrent   = release.is_current    ?? false;
    this.modalFormPrimaryAction.label = 'Salvar';
    this.modalForm.open();
  }

  saveRelease(): void {
    if (!this.formName.trim()) {
      this.notification.warning('O campo Nome é obrigatório.');
      return;
    }

    this.formLoading                  = true;
    this.modalFormPrimaryAction.loading = true;

    const input = {
      name:        this.formName.trim(),
      description: this.formDescription.trim() || undefined,
      start_date:  this.formStartDate || undefined,
      end_date:    this.formEndDate   || undefined,
      is_current:  this.formIsCurrent,
    };

    const request$ = this.formMode === 'create'
      ? this.sonarService.createRelease(input)
      : this.sonarService.updateRelease(this.selectedRelease!.id, input);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.formLoading                  = false;
        this.modalFormPrimaryAction.loading = false;
        this.modalForm.close();
        this.notification.success(
          this.formMode === 'create' ? 'Release criada com sucesso.' : 'Release atualizada com sucesso.'
        );
        if (this.formMode === 'edit' && this.selectedRelease) {
          // Atualiza o item na tabela localmente sem recarregar tudo
          this.releases = this.releases.map((r) =>
            r.id === this.selectedRelease!.id ? { ...r, ...input } : r
          );
          this.cdr.markForCheck();
        }
        this.loadReleases();
      },
      error: () => {
        this.formLoading                  = false;
        this.modalFormPrimaryAction.loading = false;
        this.notification.error(
          this.formMode === 'create'
            ? 'Erro ao criar a release. Verifique se o nome já existe.'
            : 'Erro ao atualizar a release.'
        );
        this.cdr.markForCheck();
      },
    });
  }

  deleteRelease(release: Release): void {
    this.sonarService
      .deleteRelease(release.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.notification.success(`Release "${release.name}" removida com sucesso.`);
          this.currentPage = 1;
          this.loadReleases();
        },
        error: () => this.notification.error('Erro ao remover a release.'),
      });
  }

  // ── Gerenciamento de Regras ─────────────────────────────────────────────────

  openRulesModal(release: Release): void {
    this.managedRelease  = release;
    this.linkedRules     = [];
    this.catalogItems    = [];
    this.catalogFiltered = [];
    this.catalogSearch   = '';
    this.rulesActiveTab  = 'linked';
    this.catalogPage     = 1;
    this.loadingRules    = true;
    this.modalRules.open();

    forkJoin({
      linked:  this.sonarService.getReleaseRules(release.id),
      catalog: this.sonarService.getRules({ page: 1, limit: this.catalogLimit }),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ linked, catalog }) => {
          const linkedIds = new Set(linked.map((r) => r.rule_id));

          this.linkedRules = linked.map((rr) => {
            const found = catalog.rules.find((r) => r.id === rr.rule_id);
            return {
              ...rr,
              rule_name:   found?.name ?? `Regra #${rr.rule_id}`,
              rule_key:    found?.key  ?? '—',
              editingGoal: rr.goal,
            };
          });

          this.catalogItems = catalog.rules.map((rule) => ({
            rule,
            linked:      linkedIds.has(rule.id),
            selected:    false,
            pendingGoal: linkedIds.has(rule.id).valueOf() ? linked.find((r) => r.rule_id === rule.id)?.goal ?? 'RESOLVE' : 'RESOLVE',
          }));

          this.hasMoreCatalog = 1 < catalog.totalPages;
          this.applyCatalogFilter();
          // Setar os dados com loadingRules ainda true, depois desativar o loader
          // no próximo tick: isso garante que o *ngIf crie os po-select já com
          // p-options e ngModel prontos, evitando que o valor não seja selecionado.
          this.cdr.detectChanges();
          setTimeout(() => {
            this.loadingRules = false;
            this.cdr.detectChanges();
          });
        },
        error: () => {
          this.notification.error('Erro ao carregar as regras.');
          this.loadingRules = false;
          this.cdr.markForCheck();
        },
      });
  }

  loadMoreCatalog(): void {
    if (!this.managedRelease) return;
    this.catalogPage++;
    this.sonarService
      .getRules({ page: this.catalogPage, limit: this.catalogLimit })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          const linkedIds  = new Set(this.linkedRules.map((r) => r.rule_id));
          const newItems: CatalogRuleItem[] = result.rules.map((rule) => ({
            rule,
            linked:      linkedIds.has(rule.id),
            selected:    false,
            pendingGoal: 'RESOLVE' as ReleaseGoal,
          }));
          this.catalogItems = [...this.catalogItems, ...newItems];
          this.hasMoreCatalog = this.catalogPage < result.totalPages;
          this.applyCatalogFilter();
          this.cdr.markForCheck();
        },
        error: () => this.notification.error('Erro ao carregar mais regras.'),
      });
  }

  onCatalogSearchChange(): void {
    this.applyCatalogFilter();
  }

  applyCatalogFilter(): void {
    const term = this.catalogSearch.toLowerCase().trim();
    this.catalogFiltered = term
      ? this.catalogItems.filter(
          (i) =>
            i.rule.name.toLowerCase().includes(term) ||
            i.rule.key.toLowerCase().includes(term)
        )
      : [...this.catalogItems];
  }

  get selectedCatalogItems(): CatalogRuleItem[] {
    return this.catalogItems.filter((i) => i.selected && !i.linked);
  }

  get allCatalogSelected(): boolean {
    const selectable = this.catalogFiltered.filter((i) => !i.linked);
    return selectable.length > 0 && selectable.every((i) => i.selected);
  }

  set allCatalogSelected(value: boolean) {
    this.catalogFiltered.filter((i) => !i.linked).forEach((i) => (i.selected = value));
  }

  addSelectedRules(): void {
    const toAdd = this.selectedCatalogItems;
    if (!toAdd.length) {
      this.notification.warning('Selecione ao menos uma regra para vincular.');
      return;
    }

    this.savingRules = true;
    const payload: AddReleaseRulesRequest[] = toAdd.map((i) => ({
      rule_id: i.rule.id,
      rule_key: i.rule.key,
      goal:    i.pendingGoal,
    }));

    this.sonarService
      .addReleaseRules(this.managedRelease!.id, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.savingRules = false;
          this.notification.success(`${toAdd.length} regra(s) vinculada(s) com sucesso.`);
          toAdd.forEach((item) => {
            item.linked   = true;
            item.selected = false;
            this.linkedRules = [
              ...this.linkedRules,
              {
                release_id:  this.managedRelease!.id,
                rule_id:     item.rule.id,
                goal:        item.pendingGoal,
                rule_name:   item.rule.name,
                rule_key:    item.rule.key,
                editingGoal: item.pendingGoal,
              },
            ];
          });
          this.applyCatalogFilter();
          this.cdr.markForCheck();
        },
        error: () => {
          this.savingRules = false;
          this.notification.error('Erro ao vincular regras.');
          this.cdr.markForCheck();
        },
      });
  }

  updateLinkedRuleGoal(row: LinkedRuleRow): void {
    this.sonarService
      .updateReleaseRuleGoal(row.release_id, row.rule_id, row.rule_key, row.editingGoal)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          row.goal        = updated.goal;
          row.editingGoal = updated.goal;
          this.notification.success('Goal atualizado com sucesso.');
          this.cdr.markForCheck();
        },
        error: () => this.notification.error('Erro ao atualizar o goal.'),
      });
  }

  removeLinkedRule(row: LinkedRuleRow): void {
    this.sonarService
      .deleteReleaseRule(row.release_id, row.rule_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.linkedRules = this.linkedRules.filter((r) => r.rule_id !== row.rule_id);
          const catalogItem = this.catalogItems.find((i) => i.rule.id === row.rule_id);
          if (catalogItem) catalogItem.linked = false;
          this.applyCatalogFilter();
          this.notification.success('Regra desvinculada com sucesso.');
          this.cdr.markForCheck();
        },
        error: () => this.notification.error('Erro ao desvincular a regra.'),
      });
  }

  getGoalLabel(goal: ReleaseGoal): string {
    return goal === 'RESOLVE' ? 'Zerar' : 'Zerar ou Reduzir';
  }
}

